/**
 * O grau e o fato de um risco pelo painel (E203.F4, ADR-0146 §8): o perito muda o grau (e, se
 * quiser, reescreve o fato) com fundamento; os riscos e as prioridades se reordenam pelo grau novo.
 * O risco, o uso, a resposta e a providência seguem editáveis no próprio mapa (ADR-0145).
 *
 * E203.F4.C (F-E203.F4-05): o formulário abre pelo grau e pelo fato que o documento imprime (a
 * origem da linha); sem o grau vigente, nada vem escolhido e "Salvar" espera a escolha. O fato só vai
 * quando mudou: corrigir o grau (ou só o fundamento) mantém o fato vigente.
 */
import { useState } from 'react';

import { Field, Select, Textarea } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { GRAUS_DO_RISCO, vigenteEntre, type GrauDoRisco } from '@/lib/revisaoDoDado';

import { ExplicacaoDoDado } from './ExplicacaoDoDado';
import { Fundamento, fundamentoValido } from './Fundamento';
import { BotaoSalvar, useGravacao } from './Gravacao';
import { gravarRevisao } from './gravarRevisao';
import type { PropsDoConteudo } from './tipos';

const t = ptBR.painelDoDado.grauDoRisco;

/** A chave pela âncora do achado; o número impresso (R1…) não é estável quando o grau muda. */
function chaveDoRisco(chave: string): string | null {
  return /^R\d+$/.test(chave) || chave === 'mapa' || chave === 'prioridades' ? null : chave;
}

export function RiscoNoPainel(props: PropsDoConteudo) {
  const { pedido, acoes, onMudou } = props;
  const chave = chaveDoRisco(pedido.origem.chave);
  const fatoVigente = (pedido.origem.texto ?? '').trim();
  const [grau, setGrau] = useState<GrauDoRisco | ''>(() =>
    vigenteEntre(GRAUS_DO_RISCO, pedido.origem.valor),
  );
  const [fato, setFato] = useState(fatoVigente);
  const [fundamento, setFundamento] = useState('');
  const fatoNovo = fato.trim();
  const gravacao = useGravacao(
    () =>
      gravarRevisao(props, {
        alvo: 'risco',
        chave: chave ?? '',
        valor: grau,
        texto: fatoNovo === '' || fatoNovo === fatoVigente ? null : fatoNovo,
        fundamento: fundamento.trim(),
      }),
    () => {
      acoes.salvo();
      onMudou();
    },
  );
  if (chave === null) return <ExplicacaoDoDado texto={ptBR.painelDoDado.risco.texto} {...props} />;
  return (
    <div className="space-y-sc-3" data-testid="painel-risco">
      <p className="text-body-sm text-fg-muted">{t.texto}</p>
      <Field label={t.grau} htmlFor="painel-risco-grau">
        <Select
          id="painel-risco-grau"
          value={grau}
          onChange={(e) => setGrau(vigenteEntre(GRAUS_DO_RISCO, e.target.value))}
          data-testid="painel-risco-grau"
        >
          <option value="" disabled>
            {t.escolha}
          </option>
          {GRAUS_DO_RISCO.map((g) => (
            <option key={g} value={g}>
              {t.graus[g]}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t.fato} htmlFor="painel-risco-fato">
        <Textarea
          id="painel-risco-fato"
          value={fato}
          onChange={(e) => setFato(e.target.value)}
          data-testid="painel-risco-fato"
        />
      </Field>
      <Fundamento id="painel-risco-fundamento" valor={fundamento} onChange={setFundamento} />
      <BotaoSalvar
        estado={gravacao.estado}
        recusa={gravacao.recusa}
        desligado={grau === '' || !fundamentoValido(fundamento)}
        onSalvar={() => void gravacao.salvar()}
        testId="painel-risco-salvar"
      />
    </div>
  );
}
